import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Wallet, RefreshCw, Save, Landmark } from 'lucide-react';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Opening Balance panel — Xero-driven bank accounts on the Dashboard.
 *
 * Behaviour spec:
 *  - Bank list comes from Xero on every sync (Q4: archived bank → auto-removed)
 *  - User can edit any balance inline, but next sync overwrites (Q1: no override)
 *  - Negative balances → user prompted once for credit_limit; credit_limit is
 *    NEVER auto-overwritten (only user changes it)
 *  - Available balance per account: balance + credit_limit if balance < 0, else balance
 *  - Total available across all accounts becomes opening_cash for the forecast
 */
export default function BankAccountsPanel({ activeBusiness, onSaved, compact = false }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fmt = (n) =>
    new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(Number(n) || 0);

  const computeAvailable = (a) => {
    const bal = parseFloat(a.balance) || 0;
    const limit = parseFloat(a.credit_limit) || 0;
    if (a.is_loan) {
      // Loan / overdraft facility:
      //   available = limit + balance
      // Xero sign convention: balance < 0 = amount drawn, balance > 0 = in-credit.
      // Either way `limit + balance` gives the correct remaining facility headroom.
      return limit + bal;
    }
    return bal >= 0 ? bal : bal + limit;
  };

  const total = accounts.reduce((s, a) => s + computeAvailable(a), 0);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setAccounts(res.data?.bank_accounts || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [activeBusiness]);

  const updateField = (idx, field, value) => {
    setAccounts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleaned = accounts.map((a) => ({
        name: a.name,
        balance: parseFloat(a.balance) || 0,
        credit_limit: parseFloat(a.credit_limit) || 0,
        is_loan: !!a.is_loan,
        xero_account_id: a.xero_account_id || null,
        currency: a.currency || null,
        ledger_balance: a.ledger_balance ?? null,
        outstanding_payments: a.outstanding_payments ?? null,
        outstanding_receipts: a.outstanding_receipts ?? null,
        balance_source: a.balance_source ?? null,
      }));
      await axios.put(`${API}/settings`, { bank_accounts: cleaned });
      toast.success('Bank balances updated');
      setDirty(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save bank balances');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="opening-balance-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Wallet className="w-5 h-5 text-primary" />
              Opening Balance
            </CardTitle>
            <CardDescription>
              Statement balances synced from Xero. Edit inline if needed; the next Xero sync will refresh balances.
            </CardDescription>
          </div>
          {dirty && (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
              data-testid="save-banks-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {accounts.length === 0 ? (
          <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-slate-400" />
            No bank accounts yet. Connect Xero in Settings and run a sync to import your bank accounts and statement balances.
          </div>
        ) : compact ? (
          <>
            <div className="space-y-3" data-testid="bank-accounts-grid">
              {accounts.map((a, i) => {
                const bal = parseFloat(a.balance) || 0;
                const isNegative = bal < 0;
                const limit = parseFloat(a.credit_limit) || 0;
                const available = computeAvailable(a);
                const promptCredit = !a.is_loan && isNegative && limit === 0;
                const showCreditRow = a.is_loan || promptCredit || limit > 0;
                return (
                  <div
                    key={a.xero_account_id || i}
                    className={`rounded-lg border ${a.is_loan ? 'border-amber-200 bg-amber-50/30 dark:bg-amber-900/10 dark:border-amber-900/40' : 'border-slate-200 dark:border-slate-700'} p-3 hover:border-primary/40 transition-colors`}
                    data-testid={`bank-row-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate flex items-center gap-1.5" title={a.name}>
                          {a.is_loan && <Landmark className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                          {a.name || 'Unnamed account'}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {a.is_loan ? 'Loan / Overdraft facility' : 'Bank account'}
                          {a.currency ? ` · ${a.currency}` : ''}
                        </p>
                      </div>
                      <Input
                        type="number"
                        step="0.01"
                        value={a.balance}
                        onChange={(e) => updateField(i, 'balance', e.target.value)}
                        className={`h-8 w-28 text-right font-mono text-sm font-semibold ${isNegative ? 'text-red-600 border-red-200' : ''}`}
                        data-testid={`bank-balance-${i}`}
                        title="Edit statement balance"
                      />
                    </div>
                    {/* Loan toggle */}
                    <div className="mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-slate-500 flex items-center gap-1">
                        <Landmark className="w-3 h-3 text-slate-400" />
                        Loan / overdraft
                      </span>
                      <Switch
                        checked={!!a.is_loan}
                        onCheckedChange={(v) => updateField(i, 'is_loan', v)}
                        data-testid={`bank-loan-toggle-${i}`}
                      />
                    </div>
                    {showCreditRow && (
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-500">
                          {a.is_loan ? 'Facility limit' : 'Credit limit'}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          value={a.credit_limit || 0}
                          onChange={(e) => updateField(i, 'credit_limit', e.target.value)}
                          className={`h-7 w-24 text-right font-mono text-xs ${promptCredit ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/10' : ''}`}
                          placeholder="0"
                          data-testid={`bank-credit-limit-${i}`}
                        />
                      </div>
                    )}
                    {promptCredit && (
                      <p className="text-[10px] text-amber-700 mt-1">Negative balance — set credit limit</p>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1.5 flex items-center justify-between">
                      <span>Available</span>
                      <span
                        className={`font-mono font-semibold ${available < 0 ? 'text-red-600' : 'text-emerald-600 dark:text-emerald-400'}`}
                        data-testid={`bank-available-${i}`}
                      >
                        {fmt(available)}
                      </span>
                    </p>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
                Total available
              </p>
              <p
                className={`text-2xl font-mono font-bold ${total < 0 ? 'text-red-600' : 'text-primary'}`}
                data-testid="bank-total-available"
              >
                {fmt(total)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                Forecast opening balance
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="overflow-x-auto" data-testid="bank-accounts-grid">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left font-semibold py-2 px-2">Bank account</th>
                    <th className="text-right font-semibold py-2 px-2 w-44" title="Live bank-feed statement balance from Xero (includes outstanding items). Falls back to ledger closing balance if the Bank Reconciliation report is unavailable.">
                      Statement balance
                    </th>
                    <th className="text-center font-semibold py-2 px-2 w-24" title="Toggle on for loan accounts and overdraft facilities. Affects how available balance is calculated.">
                      Loan?
                    </th>
                    <th className="text-right font-semibold py-2 px-2 w-44">Credit / Facility limit</th>
                    <th className="text-right font-semibold py-2 px-2 w-44">Available</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a, i) => {
                    const bal = parseFloat(a.balance) || 0;
                    const isNegative = bal < 0;
                    const limit = parseFloat(a.credit_limit) || 0;
                    const available = computeAvailable(a);
                    const promptCredit = !a.is_loan && isNegative && limit === 0;
                    return (
                      <React.Fragment key={a.xero_account_id || i}>
                      <tr
                        className={`border-b border-slate-100 dark:border-slate-800 last:border-0 ${a.is_loan ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}`}
                        data-testid={`bank-row-${i}`}
                      >
                        <td className="py-2 px-2">
                          <Input
                            value={a.name || ''}
                            onChange={(e) => updateField(i, 'name', e.target.value)}
                            className="h-9"
                            data-testid={`bank-name-${i}`}
                          />
                          {a.xero_account_id && (
                            <p className="text-[10px] text-slate-400 mt-0.5 px-1">
                              Synced from Xero{a.currency ? ` · ${a.currency}` : ''}
                            </p>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={a.balance}
                            onChange={(e) => updateField(i, 'balance', e.target.value)}
                            className={`h-9 text-right font-mono ${isNegative ? 'text-red-600' : ''}`}
                            data-testid={`bank-balance-${i}`}
                          />
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Switch
                            checked={!!a.is_loan}
                            onCheckedChange={(v) => updateField(i, 'is_loan', v)}
                            data-testid={`bank-loan-toggle-${i}`}
                          />
                          {a.is_loan && (
                            <p className="text-[10px] text-amber-700 mt-0.5">Loan</p>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={a.credit_limit || 0}
                            onChange={(e) => updateField(i, 'credit_limit', e.target.value)}
                            className={`h-9 text-right font-mono ${promptCredit ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/10' : ''}`}
                            placeholder={promptCredit ? 'Enter limit' : '0.00'}
                            data-testid={`bank-credit-limit-${i}`}
                          />
                          {promptCredit && (
                            <p className="text-[10px] text-amber-700 mt-0.5 px-1">
                              Negative balance — set credit limit
                            </p>
                          )}
                        </td>
                        <td className={`py-2 px-2 text-right font-mono font-semibold ${available < 0 ? 'text-red-600' : ''}`} data-testid={`bank-available-${i}`}>
                          {fmt(available)}
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Total available — used as forecast opening balance
              </span>
              <span
                className="font-mono font-semibold text-lg text-primary"
                data-testid="opening-balance-total"
              >
                {fmt(total)}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
